<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Slider, Button } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	//Components
	import IrrigationConfirmModal from '../Irrigation/IrrigationConfirmModal.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type {
		TemplateService,
		VirtualComponentConfig,
		VirtualComponentInterface,
		VirtualComponentUI,
	} from 'src/types/VirtualComponents';

	export let device: Device;
	export let setting: string;
	export let min: number = 0;
	export let max: number = 100;
	export let step: number = 1;
	export let unit: string = '%';
	export let title: string = null;
	export let disabled: boolean = false;
	export let confirmModal: IrrigationConfirmModal = null;

	let value = 0;
	let targetLock = false;
	let waitForResponse = false;
	let component: VirtualComponentInterface = null;

	const onSettingChange = (device: Device) => {
		let _value = 0;
		const templateService: TemplateService = getTemplateService(device);
		if (templateService?.settings?.[setting] != null) {
			_value = templateService.settings[setting] || 0;
		} else {
			component = templateService.components.find((i) => i.config?.role == setting);
			const config: VirtualComponentConfig = component?.config;
			const configUI: VirtualComponentUI = config?.meta?.ui;
			min = config?.min || min;
			max = config?.max || max;
			step = configUI?.step || step;
			unit = configUI?.unit || unit;
			_value = component?.status?.value || 0;
		}
		if (!waitForResponse && !targetLock && value !== _value) {
			value = _value;
		}
		if (targetLock && _value == value) {
			targetLock = false;
		}
	};

	const saveValue = async () => {
		const _value = value;
		if (confirmModal && !(await confirmModal.confirm())) {
			value = _value;
			targetLock = false;
			return;
		}
		waitForResponse = true;
		let res: SettingRequest;
		if (component) {
			res = await gen2Request(device, `${component.type}.set`, {
				id: component.config.id,
				value,
			});
		} else {
			res = await gen2Request(device, 'Service.SetConfig', {
				id: 0,
				config: {
					[`${setting}`]: value,
				},
			});
		}
		if (res?.isok) {
			value = _value;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const a11yProps = {
		ariaLabel: title || (component ? component.config?.name : setting),
		ariaLabelValue:
			(title || (component ? component.config?.name : setting)) +
			' ' +
			(value || 0) +
			(unit ? ' ' + unit : ''),
		role: 'slider',
		'aria-valuemin': min,
		'aria-valuemax': max,
		'aria-valuenow': value || 0,
		'aria-live': 'polite',
	};

	$: onSettingChange(device);
</script>

{#if title}
	<Text element="h2" styleType="h2" class="mb-s">
		{title}
	</Text>
{/if}

<Slider
	id="slider-lnmnr4f"
	{a11yProps}
	class="mb-s"
	type="simple"
	title=" "
	{min}
	{max}
	{step}
	{value}
	unit={` ${unit}`}
	disabled={waitForResponse || disabled}
	on:value_change={(e) => {
		targetLock = true;
		value = e.detail.value;
	}}
/>
<Button loading={waitForResponse} size="m" width="full" {disabled} on:click={saveValue}
	>{$format('button_save')}</Button
>
